// src/App.js
import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaStar,
  FaShieldAlt,
  FaCheckCircle,
  FaPhoneAlt,
  FaHome,
  FaTools,
  FaCloudRain,
  FaBuilding,
  FaLeaf,
  FaLayerGroup,
  FaWater,
  FaClipboardCheck,
  FaComments,
  FaHammer,
  FaArrowRight,

  // ✅ Add these:
  FaSnowflake,
  FaFire,
  FaFan,
} from "react-icons/fa";
import { Routes, Route } from "react-router-dom";
import { EstimateModal as RoofingModal } from "./EstimateModal";
import { EstimateModalInfinity } from "./EstimateModalInfinity";import infinityLogo from "./assets/infinity-logo.png";
import logo from "./assets/roofingpro-logo.webp";
import googleLogo from "./assets/google-g.png";

/** --------------------------------
 * Motion presets
 * -------------------------------- */
const section = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const fadeSwap = {
  hidden: { opacity: 0, y: 10, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.45, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -10,
    filter: "blur(8px)",
    transition: { duration: 0.25, ease: "easeIn" },
  },
};

function Stars() {
  return (
    <div className="flex items-center gap-1 text-[#D84B3F]">
      <FaStar />
      <FaStar />
      <FaStar />
      <FaStar />
      <FaStar />
    </div>
  );
}
function RoofingSite() {
  const [open, setOpen] = useState(false);

  const stats = useMemo(
    () => [
      { icon: <FaCheckCircle />, label: "Projects completed", to: 1200, suffix: "+" },
      { icon: <FaStar />, label: "5-star reviews", to: 450, suffix: "+" },
      { icon: <FaShieldAlt />, label: "Years local", to: 10, suffix: "+" },
    ],
    []
  );

  const services = useMemo(
    () => [
      {
        icon: <FaHome />,
        title: "Residential Roofing",
        summary: "Shingle and metal systems installed clean and tight.",
        detail:
          "We handle tear-offs, underlayment, ventilation, and flashing the right way. You get photo documentation and a final walkthrough so you know exactly what was done. We keep the jobsite clean and finish with sharp lines and tight penetrations. Built for Houston weather and long-term performance.",
      },
      {
        icon: <FaBuilding />,
        title: "Commercial Roofing",
        summary: "Reliable repairs and replacements for offices and buildings.",
        detail:
          "Clear communication and minimal disruption from inspection to completion. We plan staging, access, and safety so operations stay smooth. Photo documentation and clean timelines are standard. Dependable workmanship with no surprises.",
      },
      {
        icon: <FaWater />,
        title: "Gutter Repair & Install",
        summary: "Water control that protects roof, siding, and foundation.",
        detail:
          "We repair leaks, re-secure loose runs, fix downspouts, and correct drainage issues. New installs are laid out for proper pitch and clean curb appeal. We’ll recommend what makes sense based on your roofline and water flow. Reliable performance, not quick patches.",
      },
      {
        icon: <FaLeaf />,
        title: "Vegetative Roofing",
        summary: "Green roofing designed for durability and drainage.",
        detail:
          "We focus on proper waterproofing, layering, and drainage so the system performs long-term. Root barriers, edge protection, and transitions are planned to avoid failures. Documentation and clear maintenance expectations are provided. Built to look great and function correctly.",
      },
      {
        icon: <FaLayerGroup />,
        title: "Built-Up Roofing",
        summary: "Traditional multi-ply systems for proven protection.",
        detail:
          "We install built-up layers with attention to seams, penetrations, and edge details. The goal is even coverage and dependable waterproofing without shortcuts. We document conditions and provide clear options based on roof health. Built for performance in harsh conditions.",
      },
      {
        icon: <FaCloudRain />,
        title: "Storm & Hail",
        summary: "Photo-documented inspections after severe weather.",
        detail:
          "After storms, we inspect the full system and look for damage patterns that cause future leaks. You get photo proof and an honest recommendation on repair vs replacement. We’ll point out what’s cosmetic and what’s functional so you can decide cleanly. If needed, we provide documentation you can use for next steps.",
      },
    ],
    []
  );

  const reviewFeed = useMemo(
    () => [
      { name: "Alyssa M.", city: "Houston, TX", img: null, text: "They showed up on time, took photos, and explained everything clearly. No pressure at all." },
      { name: "Daniel R.", city: "Houston, TX", img: null, text: "Fast response after the storm. Great communication and the crew left everything spotless." },
      { name: "Maria T.", city: "Houston, TX", img: null, text: "Professional from start to finish. Honest recommendation and the roof looks amazing." },
      { name: "Kevin B.", city: "Katy, TX", img: null, text: "Inspection was detailed with photos. The proposal was clear, and the install crew was on time." },
      { name: "Jasmine L.", city: "Pearland, TX", img: null, text: "Quick turnaround after wind damage. They fixed the flashing and verified everything before leaving." },
      { name: "Chris W.", city: "Cypress, TX", img: null, text: "Good communication, clean jobsite, and the roof looks great. No surprises." },
      { name: "Nina S.", city: "Sugar Land, TX", img: null, text: "They explained options without pushing. The repair solved our leak immediately." },
      { name: "Mark D.", city: "Spring, TX", img: null, text: "Professional crew. They protected landscaping and cleaned up nails thoroughly." },
      { name: "Vanessa P.", city: "Houston, TX", img: null, text: "They were straight with us about what we needed and what we didn’t. Highly recommend." },
      { name: "Ethan K.", city: "Pasadena, TX", img: null, text: "On time, organized, and the final walkthrough + photos was a nice touch." },
    ],
    []
  );

  // HERO stacked reviews
  const [feedIdx, setFeedIdx] = useState(0);
  const [stack, setStack] = useState(() => {
    const a = reviewFeed[0];
    const b = reviewFeed[1];
    const c = reviewFeed[2];
    return [
      { ...a, __key: "r0" },
      { ...b, __key: "r1" },
      { ...c, __key: "r2" },
    ];
  });

  useEffect(() => {
    const t = setInterval(() => setFeedIdx((p) => p + 1), 3200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!reviewFeed.length) return;
    const next = reviewFeed[(feedIdx + 3) % reviewFeed.length];
    setStack((prev) => {
      const nextObj = { ...next, __key: `r-${feedIdx}-${next.name}` };
      return [nextObj, ...prev].slice(0, 3);
    });
  }, [feedIdx, reviewFeed]);

  // Bottom review slider (3 at a time)
  const [bottomIdx, setBottomIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setBottomIdx((p) => p + 1), 3600);
    return () => clearInterval(t);
  }, []);

  const bottomVisible = useMemo(() => {
    const a = reviewFeed[bottomIdx % reviewFeed.length];
    const b = reviewFeed[(bottomIdx + 1) % reviewFeed.length];
    const c = reviewFeed[(bottomIdx + 2) % reviewFeed.length];
    return [
      { ...a, __key: `b-${bottomIdx}-a` },
      { ...b, __key: `b-${bottomIdx}-b` },
      { ...c, __key: `b-${bottomIdx}-c` },
    ];
  }, [bottomIdx, reviewFeed]);

  return (
    <div className="min-h-screen text-white overflow-hidden">
      {/* Clean background */}
      <div className="fixed inset-0 -z-30 bg-[radial-gradient(1000px_650px_at_50%_0%,rgba(216,75,63,0.18),transparent_62%),linear-gradient(to_bottom,rgba(10,14,28,1),rgba(6,10,20,1))]" />

      {/* Minimal floating glow */}
      <div className="pointer-events-none fixed inset-0 -z-20">
        <motion.div
          className="absolute -top-28 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-white/10 blur-[120px]"
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className={`transition duration-200 ${open ? "blur-[7px] brightness-75" : ""}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-white/10 bg-black/30 backdrop-blur">
          <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Roofing Pro HTX" className="h-11 w-auto drop-shadow" />
              <div className="leading-tight">
                <p className="text-sm font-semibold tracking-tight">Roofing Pro HTX</p>
<p className="text-[10px] sm:text-[11px] text-white/65 whitespace-nowrap">
  Licensed &amp; Insured · Houston, TX
</p>              </div>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="tel:+17325541148"
                className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/85 hover:bg-white/10 transition"
              >
                <FaPhoneAlt className="text-white/60" />
                (732) 554-1148
              </a>

              <button
  onClick={() => setOpen(true)}
  className="whitespace-nowrap rounded-full bg-[#D84B3F] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(216,75,63,0.35)] hover:-translate-y-[1px] hover:bg-[#c94439] transition"
>
  Free Inspection →
</button>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-5xl px-6 pb-28">
          {/* HERO */}
          <motion.section
            variants={section}
            initial="hidden"
            animate="show"
            className="pt-16 md:pt-20 grid gap-10 md:grid-cols-2 items-stretch"
          >
            <div className="text-center md:text-left flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-white/75 w-fit mx-auto md:mx-0">
                <FaShieldAlt className="text-white/70" />
                Houston Roofing • Licensed & Insured
              </div>

              <h1 className="mt-6 text-4xl md:text-6xl font-semibold tracking-tight leading-[1.03]">
                Houston’s Most Trusted Roofers
                <span className="block text-white/70">For 10+ Years</span>
              </h1>

              <p className="mt-5 text-sm text-white/75 max-w-xl mx-auto md:mx-0">
                Free inspection with photo proof and a clear plan - Guaranteed.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <button
                  onClick={() => setOpen(true)}
                  className="rounded-full bg-[#D84B3F] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(216,75,63,0.35)] hover:-translate-y-[1px] hover:bg-[#c94439] transition"
                >
                  Schedule Free Inspection →
                </button>

                <a
                  href="#services"
                  className="rounded-full border border-white/10 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white/90 hover:bg-white/10 transition text-center"
                >
                  View Services
                </a>
              </div>
            </div>

            <div className="flex items-center">
              <ReviewStack items={stack} googleLogoSrc={googleLogo} heightClass="h-[340px]" />
            </div>
          </motion.section>

          {/* Stats */}
          <motion.section
            variants={section}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="mt-14"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              {stats.map((s) => (
                <StatCard key={s.label} icon={s.icon} label={s.label} to={s.to} suffix={s.suffix} />
              ))}
            </div>
          </motion.section>

          {/* ✅ updated steps */}
          <StepsShowcase onCTA={() => setOpen(true)} />

          {/* Services */}
          <motion.section
            id="services"
            variants={section}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="mt-20"
          >
            <div className="text-center">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                Our Services
              </p>
              <h2 className="mt-2 text-2xl md:text-3xl font-semibold">
                Roofing done right
              </h2>
              <p className="mt-3 text-sm text-white/70 max-w-xl mx-auto">
                Hover a service to see details.
              </p>
            </div>

<div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 items-start">
                {services.map((s) => (
                <ServiceCard
                  key={s.title}
                  icon={s.icon}
                  title={s.title}
                  summary={s.summary}
                  detail={s.detail}
                />
              ))}
            </div>

            <div className="mt-10 text-center">
              <button
                onClick={() => setOpen(true)}
                className="rounded-full bg-[#D84B3F] px-8 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(216,75,63,0.35)] hover:bg-[#c94439] transition"
              >
                Free Roof Inspection Today →
              </button>
            </div>
          </motion.section>

          {/* Bottom reviews */}
          <motion.section
            variants={section}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="mt-20"
          >
            <div className="text-center">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                Reviews
              </p>
              <h2 className="mt-2 text-2xl md:text-3xl font-semibold">
                Verified 5-star experiences
              </h2>
              <p className="mt-3 text-sm text-white/70 max-w-xl mx-auto">
                A rotating set of recent reviews presented with Google branding.
              </p>
            </div>

            <div className="mt-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={bottomIdx}
                  initial={{ opacity: 0, y: 12, filter: "blur(8px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -12, filter: "blur(8px)" }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="grid gap-4 md:grid-cols-3"
                >
                  {bottomVisible.map((r) => (
                    <SoftCard key={r.__key} className="p-6">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <Avatar img={r.img} name={r.name} />
                          <div className="leading-tight">
                            <p className="text-sm font-semibold text-white">{r.name}</p>
                            <p className="text-xs text-white/55">{r.city}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <img src={googleLogo} alt="Google" className="h-5 w-5 opacity-90" />
                          <Stars />
                        </div>
                      </div>

                      <p className="mt-4 text-sm text-white/80 leading-relaxed">
                        “{r.text}”
                      </p>
                    </SoftCard>
                  ))}
                </motion.div>
              </AnimatePresence>

             
            </div>
          </motion.section>

          {/* Final CTA */}
          <motion.section
            variants={section}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.6 }}
            className="mt-20"
          >
            <SoftCard className="p-8 text-center">
              <h3 className="text-xl md:text-2xl font-semibold">
                Schedule your free inspection
              </h3>
              <p className="mt-2 text-sm text-white/70">
                Quick inspection. Photo proof. Clear options.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setOpen(true)}
                  className="rounded-full bg-[#D84B3F] px-9 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(216,75,63,0.35)] hover:bg-[#c94439] transition"
                >
                  Get My Free Estimate →
                </button>
              </div>
            </SoftCard>
          </motion.section>
        </main>

        <footer className="border-t border-white/10 bg-black/20 backdrop-blur">
          <div className="mx-auto max-w-5xl px-6 py-6 text-xs text-white/55 text-center">
            © {new Date().getFullYear()} Roofing Pro HTX · Houston, TX
          </div>
        </footer>
      </div>

      {/* Modal */}
      <AnimatePresence mode="wait">
{open ? <RoofingModal open={open} onClose={() => setOpen(false)} /> : null}      </AnimatePresence>
    </div>
  );
}
function SoftCard({ children, className = "" }) {
  return (
    <div
      className={
        "rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md shadow-[0_22px_70px_rgba(0,0,0,0.30)] " +
        className
      }
    >
      {children}
    </div>
  );
}

function Avatar({ img, name }) {
  const initials = (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");

  if (img) {
    return (
      <img
        src={img}
        alt={name}
        className="h-10 w-10 rounded-full object-cover border border-white/10"
      />
    );
  }

  return (
    <div className="h-10 w-10 rounded-full border border-white/10 bg-white/10 flex items-center justify-center text-xs font-semibold text-white/80">
      {initials}
    </div>
  );
}

// small countup, no deps
function CountUp({ to = 0, duration = 1.7, suffix = "" }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0;
    let start = 0;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 2);
      setV(to * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);

  return (
    <span>
      {Math.round(v).toLocaleString()}
      {suffix}
    </span>
  );
}

function StatCard({ icon, label, to, suffix }) {
  return (
    <SoftCard className="p-6 text-center">
      <div className="mx-auto h-11 w-11 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center text-[#D84B3F] text-lg">
        {icon}
      </div>
      <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-white/55">
        {label}
      </p>
      <p className="mt-2 text-3xl font-semibold text-white">
        <CountUp to={to} suffix={suffix} />
      </p>
    </SoftCard>
  );
}

/** ✅ FIX: only hovered card grows (no row resize) */
function ServiceCard({ icon, title, summary, detail }) {
  return (
    <motion.div
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="group self-start"
    >
      {/* IMPORTANT: no h-full here */}
      <SoftCard className="p-6">
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center text-[#D84B3F] text-xl">
            {icon}
          </div>

          <p className="mt-4 text-sm font-semibold text-white">{title}</p>
          <p className="mt-2 text-sm text-white/70 max-w-[38ch]">{summary}</p>

          {/* grows the card height like it used to (no overlay) */}
          <div className="mt-4 w-full overflow-hidden">
            <div className="max-h-0 opacity-0 translate-y-2 group-hover:max-h-56 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 ease-out">
              <p className="text-xs text-white/70 leading-relaxed">{detail}</p>
            </div>
          </div>
        </div>
      </SoftCard>
    </motion.div>
  );
}
function InfinitySite() {
  const [open, setOpen] = useState(false);

  const INF_PRIMARY = "#1E90FF";
  const INF_PRIMARY_DARK = "#1877D6";

  // Override Stars color for Infinity (without touching Roofing)
  const InfinityStars = () => (
    <div className="flex items-center gap-1 text-[#1E90FF]">
      <FaStar />
      <FaStar />
      <FaStar />
      <FaStar />
      <FaStar />
    </div>
  );

  const stats = useMemo(
    () => [
      { icon: <FaCheckCircle />, label: "Projects completed", to: 1500, suffix: "+" },
      { icon: <FaStar />, label: "5-star reviews", to: 600, suffix: "+" },
      { icon: <FaShieldAlt />, label: "Years local", to: 12, suffix: "+" },
    ],
    []
  );

  const services = useMemo(
    () => [
      {
        icon: <FaWater />,
        title: "Plumbing Repair",
        summary: "Leaks, slab issues, burst pipes & emergency fixes.",
        detail:
          "We diagnose plumbing issues fast and repair them the right way—no band-aids. From leaking valves and broken fixtures to slab leaks and pipe failures, we keep the work clean, code-compliant, and built to last. Clear communication, clean finish, and we verify everything before we leave.",
      },
      {
        icon: <FaSnowflake />,
        title: "AC Repair & Install",
        summary: "Cooling diagnostics, repairs, and full replacements.",
        detail:
          "If your AC can’t keep up, is freezing, or is short-cycling, we track down the real cause—airflow, refrigerant, electrical, coils, or compressor. We’ll give you a clear repair vs replace recommendation and a clean install plan if needed. Comfort restored with professional workmanship.",
      },
      {
        icon: <FaFire />,
        title: "Heating Systems",
        summary: "Furnace and heat pump repair + replacement.",
        detail:
          "Reliable heat matters. We service furnaces and heat pumps with a focus on safety, efficiency, and proper airflow. If replacement makes sense, we size the system correctly and install it cleanly with tight communication from start to finish.",
      },
      {
        icon: <FaTools />,
        title: "Water Heaters",
        summary: "Tank and tankless installs, repairs, and upgrades.",
        detail:
          "No hot water? We repair and replace gas and electric water heaters—tank and tankless. Proper venting, code compliance, clean plumbing connections, and a setup that’s built for long-term reliability. We’ll recommend what makes sense based on your household usage.",
      },
      {
        icon: <FaFan />,
        title: "Indoor Air Quality",
        summary: "Filtration upgrades, airflow optimization, comfort fixes.",
        detail:
          "Comfort isn’t just temperature—air quality and airflow matter. We improve filtration, address airflow restrictions, and help reduce dust and allergens where possible. We’ll recommend upgrades that actually move the needle, not expensive fluff.",
      },
      {
        icon: <FaCloudRain />,
        title: "Emergency Service",
        summary: "Urgent plumbing/HVAC response when you need it most.",
        detail:
          "When something fails suddenly—burst pipe, AC down in the heat, water heater leaking—we move quickly. Rapid dispatch, clean work, and clear options so you can make a decision fast and get back to normal.",
      },
    ],
    []
  );

  const reviewFeed = useMemo(
    () => [
      { name: "Jason M.", city: "Houston, TX", img: null, text: "They fixed our AC same day. Clean, professional, and explained everything." },
      { name: "Lauren T.", city: "Katy, TX", img: null, text: "Water heater install was smooth and fast. Pricing was clear and fair." },
      { name: "Carlos R.", city: "Houston, TX", img: null, text: "Honest diagnosis and no upsells. Communication was excellent." },
      { name: "Megan P.", city: "Cypress, TX", img: null, text: "Quick plumbing repair, clean work, and they verified everything before leaving." },
      { name: "David L.", city: "Sugar Land, TX", img: null, text: "Heating repair done right the first time. Professional crew." },
      { name: "Angela S.", city: "Pearland, TX", img: null, text: "AC replacement was efficient and the install looked clean. Great experience." },
      { name: "Bryan K.", city: "Spring, TX", img: null, text: "They found the real issue fast and got us back up. Super responsive." },
      { name: "Tanya W.", city: "Houston, TX", img: null, text: "Great service and straightforward pricing. Highly recommend." },
      { name: "Oscar P.", city: "Pasadena, TX", img: null, text: "Professional and respectful. Left the jobsite spotless." },
      { name: "Nina C.", city: "Katy, TX", img: null, text: "Fast turnaround and clear options. No pressure, just facts." },
    ],
    []
  );

  // HERO stacked reviews (same logic as RoofingSite)
  const [feedIdx, setFeedIdx] = useState(0);
  const [stack, setStack] = useState(() => {
    const a = reviewFeed[0];
    const b = reviewFeed[1];
    const c = reviewFeed[2];
    return [
      { ...a, __key: "r0" },
      { ...b, __key: "r1" },
      { ...c, __key: "r2" },
    ];
  });

  useEffect(() => {
    const t = setInterval(() => setFeedIdx((p) => p + 1), 3200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!reviewFeed.length) return;
    const next = reviewFeed[(feedIdx + 3) % reviewFeed.length];
    setStack((prev) => {
      const nextObj = { ...next, __key: `r-${feedIdx}-${next.name}` };
      return [nextObj, ...prev].slice(0, 3);
    });
  }, [feedIdx, reviewFeed]);

  // Bottom review slider (3 at a time) (same as RoofingSite)
  const [bottomIdx, setBottomIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setBottomIdx((p) => p + 1), 3600);
    return () => clearInterval(t);
  }, []);

  const bottomVisible = useMemo(() => {
    const a = reviewFeed[bottomIdx % reviewFeed.length];
    const b = reviewFeed[(bottomIdx + 1) % reviewFeed.length];
    const c = reviewFeed[(bottomIdx + 2) % reviewFeed.length];
    return [
      { ...a, __key: `b-${bottomIdx}-a` },
      { ...b, __key: `b-${bottomIdx}-b` },
      { ...c, __key: `b-${bottomIdx}-c` },
    ];
  }, [bottomIdx, reviewFeed]);

  return (
    <div className="min-h-screen text-white overflow-hidden">
      {/* Clean background (blue) */}
      <div className="fixed inset-0 -z-30 bg-[radial-gradient(1000px_650px_at_50%_0%,rgba(30,144,255,0.18),transparent_62%),linear-gradient(to_bottom,rgba(10,14,28,1),rgba(6,10,20,1))]" />

      {/* Minimal floating glow (same, but keep it neutral) */}
      <div className="pointer-events-none fixed inset-0 -z-20">
        <motion.div
          className="absolute -top-28 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-white/10 blur-[120px]"
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className={`transition duration-200 ${open ? "blur-[7px] brightness-75" : ""}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-white/10 bg-black/30 backdrop-blur">
          <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={infinityLogo} alt="Infinity Air & Water" className="h-11 w-auto drop-shadow" />
              <div className="leading-tight">
                <p className="text-sm font-semibold tracking-tight">Infinity Air & Water</p>
                <p className="text-[10px] sm:text-[11px] text-white/65 whitespace-nowrap">
                  Licensed &amp; Insured · Houston, TX
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="tel:+17325541148"
                className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/85 hover:bg-white/10 transition"
              >
                <FaPhoneAlt className="text-white/60" />
                (732) 554-1148
              </a>

              <button
                onClick={() => setOpen(true)}
                className="whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(30,144,255,0.35)] hover:-translate-y-[1px] transition"
                style={{ background: INF_PRIMARY }}
                onMouseEnter={(e) => (e.currentTarget.style.background = INF_PRIMARY_DARK)}
                onMouseLeave={(e) => (e.currentTarget.style.background = INF_PRIMARY)}
              >
                Free Estimate →
              </button>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-5xl px-6 pb-28">
          {/* HERO (same motion section as Roofing) */}
          <motion.section
            variants={section}
            initial="hidden"
            animate="show"
            className="pt-16 md:pt-20 grid gap-10 md:grid-cols-2 items-stretch"
          >
            <div className="text-center md:text-left flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-white/75 w-fit mx-auto md:mx-0">
                <FaShieldAlt className="text-white/70" />
                Houston Plumbing • HVAC · Licensed & Insured
              </div>

              <h1 className="mt-6 text-4xl md:text-6xl font-semibold tracking-tight leading-[1.03]">
                Houston’s Trusted Plumbing & HVAC
                <span className="block text-white/70">Comfort + Water Systems Done Right</span>
              </h1>

              <p className="mt-5 text-sm text-white/75 max-w-xl mx-auto md:mx-0">
                Fast response with clear options and clean execution — guaranteed.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <button
                  onClick={() => setOpen(true)}
                  className="rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(30,144,255,0.35)] hover:-translate-y-[1px] transition"
                  style={{ background: INF_PRIMARY }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = INF_PRIMARY_DARK)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = INF_PRIMARY)}
                >
                  Schedule Service →
                </button>

                <a
                  href="#services"
                  className="rounded-full border border-white/10 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white/90 hover:bg-white/10 transition text-center"
                >
                  View Services
                </a>
              </div>
            </div>

            <div className="flex items-center">
              {/* Reuse ReviewStack but swap Stars via local override */}
              <SoftCard className="p-6 h-full">
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">Reviews</p>
                    <p className="mt-1 text-sm font-semibold text-white">Trusted by Houston homeowners</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <img src={googleLogo} alt="Google" className="h-5 w-5 opacity-90" />
                    <InfinityStars />
                  </div>
                </div>

                {/* exactly 2 cards visible (same behavior as ReviewStack) */}
                <div className="mt-5 relative overflow-hidden" style={{ height: 340 }}>
                  <AnimatePresence mode="popLayout">
                    {stack.map((r, i) => (
                      <motion.div
                        key={r.__key}
                        layout
                        initial={{ opacity: 0, y: -14, filter: "blur(8px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        exit={{ opacity: 0, y: 14, filter: "blur(8px)" }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className={`rounded-2xl border border-white/10 bg-black/20 p-4 ${i !== 0 ? "mt-3" : ""}`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar img={r.img} name={r.name} />
                          <div className="leading-tight">
                            <p className="text-sm font-semibold text-white">{r.name}</p>
                            <p className="text-xs text-white/55">{r.city}</p>
                          </div>
                        </div>

                        <div className="mt-3">
                          <InfinityStars />
                          <p className="mt-2 text-sm text-white/85 leading-relaxed line-clamp-3">
                            “{r.text}”
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </SoftCard>
            </div>
          </motion.section>

          {/* Stats (same) */}
          <motion.section
            variants={section}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="mt-14"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              {stats.map((s) => (
                <SoftCard key={s.label} className="p-6 text-center">
                  <div className="mx-auto h-11 w-11 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center text-[#1E90FF] text-lg">
                    {s.icon}
                  </div>
                  <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-white/55">
                    {s.label}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-white">
                    <CountUp to={s.to} suffix={s.suffix} />
                  </p>
                </SoftCard>
              ))}
            </div>
          </motion.section>

          {/* Steps (same section structure, but Infinity themed) */}
          <InfinityStepsShowcase
            onCTA={() => setOpen(true)}
            primary={INF_PRIMARY}
            primaryShadow="rgba(30,144,255,0.35)"
          />

          {/* Services */}
          <motion.section
            id="services"
            variants={section}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="mt-20"
          >
            <div className="text-center">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                Our Services
              </p>
              <h2 className="mt-2 text-2xl md:text-3xl font-semibold">
                HVAC & Plumbing done right
              </h2>
              <p className="mt-3 text-sm text-white/70 max-w-xl mx-auto">
                Hover a service to see details.
              </p>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 items-start">
              {services.map((s) => (
                <motion.div
                  key={s.title}
                  whileHover={{ y: -3 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="group self-start"
                >
                  <SoftCard className="p-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="h-12 w-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center text-[#1E90FF] text-xl">
                        {s.icon}
                      </div>

                      <p className="mt-4 text-sm font-semibold text-white">{s.title}</p>
                      <p className="mt-2 text-sm text-white/70 max-w-[38ch]">{s.summary}</p>

                      <div className="mt-4 w-full overflow-hidden">
                        <div className="max-h-0 opacity-0 translate-y-2 group-hover:max-h-56 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 ease-out">
                          <p className="text-xs text-white/70 leading-relaxed">{s.detail}</p>
                        </div>
                      </div>
                    </div>
                  </SoftCard>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <button
                onClick={() => setOpen(true)}
                className="rounded-full px-8 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(30,144,255,0.35)] hover:-translate-y-[1px] transition"
                style={{ background: INF_PRIMARY }}
                onMouseEnter={(e) => (e.currentTarget.style.background = INF_PRIMARY_DARK)}
                onMouseLeave={(e) => (e.currentTarget.style.background = INF_PRIMARY)}
              >
                Free Estimate Today →
              </button>
            </div>
          </motion.section>

          {/* Bottom reviews (same as Roofing) */}
          <motion.section
            variants={section}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="mt-20"
          >
            <div className="text-center">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">Reviews</p>
              <h2 className="mt-2 text-2xl md:text-3xl font-semibold">Verified 5-star experiences</h2>
              <p className="mt-3 text-sm text-white/70 max-w-xl mx-auto">
                A rotating set of recent reviews presented with Google branding.
              </p>
            </div>

            <div className="mt-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={bottomIdx}
                  initial={{ opacity: 0, y: 12, filter: "blur(8px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -12, filter: "blur(8px)" }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="grid gap-4 md:grid-cols-3"
                >
                  {bottomVisible.map((r) => (
                    <SoftCard key={r.__key} className="p-6">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <Avatar img={r.img} name={r.name} />
                          <div className="leading-tight">
                            <p className="text-sm font-semibold text-white">{r.name}</p>
                            <p className="text-xs text-white/55">{r.city}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <img src={googleLogo} alt="Google" className="h-5 w-5 opacity-90" />
                          <InfinityStars />
                        </div>
                      </div>

                      <p className="mt-4 text-sm text-white/80 leading-relaxed">“{r.text}”</p>
                    </SoftCard>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.section>

          {/* Final CTA (same structure) */}
          <motion.section
            variants={section}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.6 }}
            className="mt-20"
          >
            <SoftCard className="p-8 text-center">
              <h3 className="text-xl md:text-2xl font-semibold">
                Schedule your service
              </h3>
              <p className="mt-2 text-sm text-white/70">
                Quick visit. Clear options. Clean work.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setOpen(true)}
                  className="rounded-full px-9 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(30,144,255,0.35)] hover:-translate-y-[1px] transition"
                  style={{ background: INF_PRIMARY }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = INF_PRIMARY_DARK)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = INF_PRIMARY)}
                >
                  Get My Free Estimate →
                </button>
              </div>
            </SoftCard>
          </motion.section>
        </main>

        <footer className="border-t border-white/10 bg-black/20 backdrop-blur">
          <div className="mx-auto max-w-5xl px-6 py-6 text-xs text-white/55 text-center">
            © {new Date().getFullYear()} Infinity Air & Water · Houston, TX
          </div>
        </footer>
      </div>

      {/* Modal */}
      <AnimatePresence mode="wait">
        {open ? <EstimateModalInfinity open={open} onClose={() => setOpen(false)} /> : null}
      </AnimatePresence>
    </div>
  );
}

/** Drop-in StepsShowcase clone but Infinity themed (keeps SAME layout/behavior) */
function InfinityStepsShowcase({ onCTA, primary = "#1E90FF", primaryShadow = "rgba(30,144,255,0.35)" }) {
  const steps = useMemo(
    () => [
      {
        id: 0,
        eyebrow: "Step 1",
        title: "Quick visit with clear diagnosis",
        body:
          "Tell us what’s happening and we’ll schedule a quick on-site visit. We check the system, document key findings, and explain what’s actually going on in plain English.",
        icon: <FaClipboardCheck />,
      },
      {
        id: 1,
        eyebrow: "Step 2",
        title: "Review repair vs replacement options",
        body:
          "We walk you through the best next step—repair, upgrade, or replacement—based on performance, cost, and longevity. No pressure, just clear options and clean pricing.",
        icon: <FaComments />,
      },
      {
        id: 2,
        eyebrow: "Step 3",
        title: "Fast, clean execution",
        body:
          "If you move forward, we complete the work with clean execution and tight communication. We verify everything, walk you through results, and leave the space clean.",
        icon: <FaHammer />,
      },
    ],
    []
  );

  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(null);
  const shown = hovered ?? active;

  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((p) => (p + 1) % steps.length), 4200);
    return () => clearInterval(t);
  }, [paused, steps.length]);

  const progressPct = ((active + 1) / steps.length) * 100;

  return (
    <motion.section
      variants={section}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      className="mt-20"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="text-center">
        <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">The Process</p>
        <h2 className="mt-2 text-2xl md:text-3xl font-semibold">A cleaner way to get service done</h2>
        <p className="mt-3 text-sm text-white/70 max-w-xl mx-auto">
          Hover a step to preview it. Click to lock it in.
        </p>
      </div>

      <div className="mt-10 grid gap-4 md:grid-cols-[1fr,1.1fr]">
        <SoftCard className="p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-white">Steps</p>
            <p className="text-xs text-white/55">{paused ? "Paused" : "Auto"}</p>
          </div>

          <div className="mt-5">
            <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
              <motion.div
                className="h-full"
                style={{ background: primary }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              />
            </div>

            <div className="mt-6 space-y-3">
              {steps.map((s, i) => {
                const isActive = i === active;
                const isHover = i === hovered;
                const isHot = isHover || isActive;

                return (
                  <button
                    key={s.id}
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                    className={
                      "w-full text-left rounded-2xl border transition p-4 " +
                      (isHot ? "border-white/20 bg-white/10" : "border-white/10 bg-white/5 hover:bg-white/10")
                    }
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="h-10 w-10 rounded-2xl border flex items-center justify-center text-lg transition"
                        style={{
                          borderColor: isHot ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)",
                          background: isHot ? primary : "rgba(255,255,255,0.05)",
                          color: isHot ? "white" : primary,
                        }}
                      >
                        {s.icon}
                      </div>

                      <div className="flex-1">
                        <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">{s.eyebrow}</p>
                        <p className="mt-1 text-sm font-semibold text-white">{s.title}</p>
                      </div>

                      <FaArrowRight className={isHot ? "text-white/70" : "text-white/30"} />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-center">
              <button
                onClick={onCTA}
                className="rounded-full px-7 py-3 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(30,144,255,0.35)] hover:-translate-y-[1px] transition"
                style={{ background: primary }}
              >
                Request My Free Estimate →
              </button>
            </div>
          </div>
        </SoftCard>

        <SoftCard className="p-6">
          <p className="text-sm font-semibold text-white text-center">What to expect</p>

          <div className="mt-5 relative overflow-hidden rounded-2xl border border-white/10">
            <AnimatePresence mode="wait">
              <motion.div
                key={shown}
                variants={fadeSwap}
                initial="hidden"
                animate="show"
                exit="exit"
                className="p-6 flex flex-col items-center text-center"
              >
                <div className="h-12 w-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center text-lg" style={{ color: primary }}>
                  {steps[shown].icon}
                </div>

                <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-white/55">{steps[shown].eyebrow}</p>
                <p className="mt-1 text-lg font-semibold text-white">{steps[shown].title}</p>

                <p className="mt-4 text-sm text-white/75 leading-relaxed max-w-[60ch]">
                  {steps[shown].body}
                </p>

                <div className="mt-6 flex flex-wrap justify-center gap-2 text-xs text-white/55">
                  <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                    Photo proof included
                  </span>
                  <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                    Clear options
                  </span>
                  <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                    Clean worksite
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </SoftCard>
      </div>
    </motion.section>
  );
}
/**
 * Reviews stack
 */
function ReviewStack({ items, googleLogoSrc }) {
  const wrapRef = useRef(null);
  const firstCardRef = useRef(null);
  const [twoHeight, setTwoHeight] = useState(340);

  useLayoutEffect(() => {
    const calc = () => {
      const wrap = wrapRef.current;
      const first = firstCardRef.current;
      if (!wrap || !first) return;

      const firstRect = first.getBoundingClientRect();

      // Tailwind mt-3 = 0.75rem = 12px (default)
      // We’ll read the actual computed margin to be safe:
      const second = first.nextElementSibling;
      const gap =
        second ? parseFloat(getComputedStyle(second).marginTop || "0") : 12;

      // Height for 2 cards + the gap between them
      const target = Math.ceil(firstRect.height * 2 + gap);

      // Clamp so it doesn’t get tiny on short content
      setTwoHeight(Math.max(260, target));
    };

    calc();

    const ro = new ResizeObserver(calc);
    if (wrapRef.current) ro.observe(wrapRef.current);
    if (firstCardRef.current) ro.observe(firstCardRef.current);

    window.addEventListener("resize", calc);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", calc);
    };
  }, [items]);

  return (
    <SoftCard className="p-6 h-full">
      <div className="flex items-center justify-between">
        <div className="text-left">
          <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">
            Reviews
          </p>
          <p className="mt-1 text-sm font-semibold text-white">
            Trusted by Houston homeowners
          </p>
        </div>
        <div className="flex items-center gap-2">
          <img src={googleLogoSrc} alt="Google" className="h-5 w-5 opacity-90" />
          <Stars />
        </div>
      </div>

      {/* exactly 2 cards visible */}
      <div
        ref={wrapRef}
        className="mt-5 relative overflow-hidden"
        style={{ height: twoHeight }}
      >
        <AnimatePresence mode="popLayout">
          {items.map((r, i) => (
            <motion.div
              key={r.__key}
              layout
              initial={{ opacity: 0, y: -14, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: 14, filter: "blur(8px)" }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              ref={i === 0 ? firstCardRef : undefined}
              className={`rounded-2xl border border-white/10 bg-black/20 p-4 ${
                i !== 0 ? "mt-3" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <Avatar img={r.img} name={r.name} />
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-white">{r.name}</p>
                  <p className="text-xs text-white/55">{r.city}</p>
                </div>
              </div>

              <div className="mt-3">
                <Stars />
                <p className="mt-2 text-sm text-white/85 leading-relaxed line-clamp-3">
                  “{r.text}”
                </p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </SoftCard>
  );
}

/** ✅ FIX: Steps panel shows hovered step only. No Google/Stars. No tip. */
function StepsShowcase({ onCTA }) {
  const steps = useMemo(
    () => [
      {
        id: 0,
        eyebrow: "Step 1",
        title: "Free inspection with photo proof",
        body:
          "Tell us what you’re seeing and we’ll schedule a quick on-site inspection. We document key areas with photos and give you a clear summary you can actually understand.",
        icon: <FaClipboardCheck />,
      },
      {
        id: 1,
        eyebrow: "Step 2",
        title: "Review the best option for your roof",
        body:
          "We walk you through repair vs replacement, timelines, and what matters most for durability. You’ll get straightforward recommendations based on roof condition — not upsells.",
        icon: <FaComments />,
      },
      {
        id: 2,
        eyebrow: "Step 3",
        title: "Fast, clean execution",
        body:
          "If you move forward, we lock in a plan and handle the job with a clean worksite and tight communication. Final walkthrough included so you know it’s done right.",
        icon: <FaHammer />,
      },
    ],
    []
  );

  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(null);
  const shown = hovered ?? active;

  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(() => {
      setActive((p) => (p + 1) % steps.length);
    }, 4200);
    return () => clearInterval(timerRef.current);
  }, [paused, steps.length]);

  const progressPct = ((active + 1) / steps.length) * 100;

  return (
    <motion.section
      variants={section}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      className="mt-20"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="text-center">
        <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">
          The Process
        </p>
        <h2 className="mt-2 text-2xl md:text-3xl font-semibold">
          A cleaner way to get roofing done
        </h2>
        <p className="mt-3 text-sm text-white/70 max-w-xl mx-auto">
          Hover a step to preview it. Click to lock it in.
        </p>
      </div>

      <div className="mt-10 grid gap-4 md:grid-cols-[1fr,1.1fr]">
        {/* Left: linked step rail */}
        <SoftCard className="p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-white">Steps</p>
            <p className="text-xs text-white/55">{paused ? "Paused" : "Auto"}</p>
          </div>

          <div className="mt-5">
            {/* Progress bar */}
            <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
              <motion.div
                className="h-full bg-[#D84B3F]"
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              />
            </div>

            <div className="mt-6 space-y-3">
              {steps.map((s, i) => {
                const isActive = i === active;
                const isHover = i === hovered;
                const isHot = isHover || isActive;

                return (
                  <button
                    key={s.id}
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                    className={
                      "w-full text-left rounded-2xl border transition p-4 " +
                      (isHot
                        ? "border-white/20 bg-white/10"
                        : "border-white/10 bg-white/5 hover:bg-white/10")
                    }
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={
                          "h-10 w-10 rounded-2xl border flex items-center justify-center text-lg transition " +
                          (isHot
                            ? "border-white/20 bg-[#D84B3F] text-white"
                            : "border-white/10 bg-white/5 text-[#D84B3F]")
                        }
                      >
                        {s.icon}
                      </div>

                      <div className="flex-1">
                        <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                          {s.eyebrow}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-white">
                          {s.title}
                        </p>
                      </div>

                      <FaArrowRight className={isHot ? "text-white/70" : "text-white/30"} />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-center">
              <button
                onClick={onCTA}
                className="rounded-full bg-[#D84B3F] px-7 py-3 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(216,75,63,0.35)] hover:bg-[#c94439] transition"
              >
                Request My Free Inspection →
              </button>
            </div>
          </div>
        </SoftCard>

        {/* Right: hovered/active panel */}
        {/* Right: active panel (centered, no bg) */}
<SoftCard className="p-6">
  <p className="text-sm font-semibold text-white text-center">What to expect</p>

  <div className="mt-5 relative overflow-hidden rounded-2xl border border-white/10">
    <AnimatePresence mode="wait">
      <motion.div
        key={shown}
        variants={fadeSwap}
        initial="hidden"
        animate="show"
        exit="exit"
        className="p-6 flex flex-col items-center text-center"
      >
        <div className="h-12 w-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center text-[#D84B3F] text-lg">
          {steps[shown].icon}
        </div>

        <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-white/55">
          {steps[shown].eyebrow}
        </p>
        <p className="mt-1 text-lg font-semibold text-white">
          {steps[shown].title}
        </p>

        <p className="mt-4 text-sm text-white/75 leading-relaxed max-w-[60ch]">
          {steps[shown].body}
        </p>

        <div className="mt-6 flex flex-wrap justify-center gap-2 text-xs text-white/55">
          <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
            Photo proof included
          </span>
          <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
            Clear options
          </span>
          <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
            Clean worksite
          </span>
        </div>
      </motion.div>
    </AnimatePresence>
  </div>
</SoftCard>
      </div>
    </motion.section>
  );
}
export default function App() {
  return (
    <Routes>
      <Route path="/" element={<RoofingSite />} />
      <Route path="/infinity" element={<InfinitySite />} />
    </Routes>
  );
}
